/**
 * Detects stale deployments and prompts users to refresh.
 *
 * - Checks for new versions every 5 minutes
 * - Checks when the user returns to the tab (visibility change)
 * - Auto-reloads if the tab was inactive for 30+ minutes and a new version is live
 * - Otherwise surfaces a dismissible banner
 */

import { computed, onMounted, onUnmounted, ref } from 'vue'
import { usePostHog } from '@/composables/use-posthog'

interface VersionInfo {
  buildId: string
  buildTime: string
}

const CHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const AUTO_RELOAD_INACTIVE_MS = 30 * 60 * 1000 // 30 minutes

// Module-level singletons: one checker and one banner state shared by every caller.
const newVersionAvailable = ref(false)
const newBuildId = ref<string | null>(null)
const bannerDismissed = ref(false)

// Track when the user last interacted with the tab.
let lastActiveTime = Date.now()
let checkIntervalId: ReturnType<typeof setInterval> | null = null
let started = false

/**
 * Fetch the latest version from the server.
 */
const fetchLatestVersion = async (): Promise<VersionInfo | null> => {
  try {
    const response = await fetch('/version.json', {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })

    if (!response.ok) {
      console.warn('[version-check] Failed to fetch version.json:', response.status)
      return null
    }

    return await response.json()
  } catch (error) {
    console.warn('[version-check] Error fetching version:', error)
    return null
  }
}

export const useVersionCheck = () => {
  const currentBuildId = __APP_BUILD_ID__
  const { trackEvent } = usePostHog()

  /**
   * Check if a new version is available and handle accordingly.
   */
  const checkVersion = async (options: { forceReloadIfInactive?: boolean } = {}) => {
    const versionInfo = await fetchLatestVersion()
    if (!versionInfo) return

    const isNewVersion = versionInfo.buildId !== currentBuildId

    if (isNewVersion && !newVersionAvailable.value) {
      newVersionAvailable.value = true
      newBuildId.value = versionInfo.buildId

      const inactiveTimeMs = Date.now() - lastActiveTime
      const wasInactiveForLong = inactiveTimeMs >= AUTO_RELOAD_INACTIVE_MS

      trackEvent('error_stale_version_detected', {
        current_build_id: currentBuildId,
        new_build_id: versionInfo.buildId,
        will_auto_reload: wasInactiveForLong && (options.forceReloadIfInactive ?? false),
        inactive_time_minutes: Math.floor(inactiveTimeMs / 60_000),
      })

      // Auto-reload if the tab was inactive for 30+ minutes.
      if (wasInactiveForLong && options.forceReloadIfInactive) {
        // Small delay to ensure any pending work completes.
        setTimeout(() => {
          trackEvent('error_stale_version_reloaded', {
            current_build_id: currentBuildId,
            new_build_id: versionInfo.buildId,
            mode: 'auto',
          })
          window.location.reload()
        }, 100)
      }
    }
  }

  /**
   * Handle visibility change - user returned to tab.
   */
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // User returned to tab - check with auto-reload option.
      checkVersion({ forceReloadIfInactive: true })
    } else {
      // User left tab - update last active time.
      lastActiveTime = Date.now()
    }
  }

  /**
   * Manually refresh the page.
   */
  const refreshApp = () => {
    trackEvent('error_stale_version_reloaded', {
      current_build_id: currentBuildId,
      new_build_id: newBuildId.value,
      mode: 'manual',
    })
    window.location.reload()
  }

  /**
   * Dismiss the update banner.
   */
  const dismissBanner = () => {
    bannerDismissed.value = true
  }

  /**
   * Start the version checking process.
   */
  const startVersionCheck = () => {
    if (started) return
    started = true

    // Initial check (without auto-reload on first load).
    checkVersion({ forceReloadIfInactive: false })

    // Set up periodic checking.
    checkIntervalId = setInterval(() => {
      checkVersion({ forceReloadIfInactive: false })
    }, CHECK_INTERVAL_MS)

    // Listen for visibility changes.
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  /**
   * Stop the version checking process.
   */
  const stopVersionCheck = () => {
    if (checkIntervalId) {
      clearInterval(checkIntervalId)
      checkIntervalId = null
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    started = false
  }

  onMounted(() => {
    startVersionCheck()
  })

  onUnmounted(() => {
    stopVersionCheck()
  })

  return {
    // State
    newVersionAvailable: computed(() => newVersionAvailable.value),
    showBanner: computed(() => newVersionAvailable.value && !bannerDismissed.value),
    currentBuildId,
    newBuildId: computed(() => newBuildId.value),

    // Actions
    checkVersion,
    refreshApp,
    dismissBanner,
    startVersionCheck,
    stopVersionCheck,
  }
}
